import { useEffect, useState } from "react";
import { Lock, Loader2, Send } from "lucide-react";
import { toast } from "sonner";
import { day } from "@/lib/money";
import FilePick from "@/components/FilePick";
import Attachment from "@/components/community/Attachment";

const STATUS = { new: ["Sent", "text-muted-foreground"], reviewed: ["Reviewed", "text-[#60A5FA]"], done: ["Done", "text-[#4ADE80]"] };

export default function SuggestionForm({ api }) {
  const [text, setText] = useState("");
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [mine, setMine] = useState([]);
  useEffect(() => {
    api.get("/suggestions/mine").then(({ data }) => setMine(data.suggestions)).catch(() => {});
  }, [api]);

  const send = async () => {
    if (text.trim().length < 3 && !file) return toast.error("Write a little more before sending.");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("message", text.trim());
      if (file) fd.append("file", file);
      const { data } = await api.post("/suggestions", fd);
      setMine((m) => [data, ...m]);
      setText("");
      setFile(null);
      toast.success("Thanks! Your suggestion was sent to the CarrierLaunch team.");
    } catch (e) {
      const d = e.response?.data?.detail;
      toast.error(typeof d === "string" ? d : "Could not send. Try again.");
    } finally {
      setBusy(false);
    }
    return null;
  };

  return (
    <div className="grid lg:grid-cols-[minmax(0,1fr)_360px] gap-6">
      <section className="brutal-border p-4 sm:p-6" data-testid="suggestion-form">
        <h2 className="font-heading font-black text-2xl">Help us improve CarrierLaunch.</h2>
        <p className="text-sm text-muted-foreground mt-2 inline-flex items-start gap-1.5">
          <Lock size={14} className="mt-0.5 shrink-0" /> Only the CarrierLaunch team can read what you send here. Other clients can't see it.
        </p>
        <textarea value={text} onChange={(e) => setText(e.target.value)} maxLength={2000} rows={6} data-testid="suggestion-input"
          placeholder="What would make the app better for you? A feature, a fix, something confusing…"
          className="mt-4 w-full px-3 py-2.5 bg-[#141414] border border-[#262626] text-white text-base sm:text-sm resize-y focus:outline-none focus:border-white" />
        <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <FilePick file={file} onFile={setFile} testid="suggestion-attach" label="Attach screenshot or PDF" />
            <span className="text-[11px] text-muted-foreground" data-testid="suggestion-count">{text.length}/2000</span>
          </div>
          <button onClick={send} disabled={busy || (!text.trim() && !file)} data-testid="suggestion-submit"
            className="min-h-[44px] px-5 cta-primary font-mono-label text-[11px] inline-flex items-center gap-2 disabled:opacity-50">
            {busy ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Send suggestion
          </button>
        </div>
      </section>
      <section className="brutal-border p-4 sm:p-5 min-w-0" data-testid="suggestion-mine">
        <div className="font-mono-label text-[10px] text-muted-foreground mb-3">YOUR SUGGESTIONS · {mine.length}</div>
        {mine.length === 0 && <p className="text-sm text-muted-foreground" data-testid="suggestion-mine-empty">You haven't sent any yet.</p>}
        <div className="divide-y divide-[#262626]">
          {mine.map((s) => (
            <div key={s.id} className="py-2.5" data-testid={`suggestion-mine-${s.id}`}>
              <div className="flex items-center justify-between gap-2 text-[11px]">
                <span className="text-muted-foreground">{day(s.created_at)}</span>
                <span className={`font-mono-label text-[10px] ${STATUS[s.status]?.[1]}`}>{STATUS[s.status]?.[0]}</span>
              </div>
              <p className="text-sm mt-1 whitespace-pre-wrap break-words line-clamp-4">{s.message}</p>
              {s.attachment && <Attachment api={api} url={`/suggestions/${s.id}/file`} att={s.attachment} testid={`suggestion-mine-att-${s.id}`} />}
              {s.reply && (
                <div className="mt-2 border-l-2 border-[#FFD600] pl-3" data-testid={`suggestion-mine-reply-${s.id}`}>
                  <div className="text-[11px] text-[#FFD600]">CarrierLaunch team replied · {day(s.replied_at)}</div>
                  <p className="text-sm whitespace-pre-wrap break-words">{s.reply}</p>
                  {s.reply_attachment && <Attachment api={api} url={`/suggestions/${s.id}/file?which=reply`} att={s.reply_attachment} testid={`suggestion-mine-reply-att-${s.id}`} />}
                </div>
              )}
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
